import React, { useCallback, useEffect, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useCurrentTenant } from '../lib/TenantBrandingContext';
import { api } from '../lib/api';

const NOTIFICATIONS_POLL_MS = 60_000;

const formatNotificationTime = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 60_000) return 'just now';
    if (diffMs < 60 * 60_000) return `${Math.floor(diffMs / 60_000)}m ago`;
    if (diffMs < 24 * 60 * 60_000) return `${Math.floor(diffMs / (60 * 60_000))}h ago`;
    return d.toLocaleDateString('en-GB');
  } catch {
    return '';
  }
};

const NavBar = ({
  user,
  token,
  currentView,
  onViewChange,
  onOpenCaseLibrary,
  onOpenAdminSettings,
  onOpenPlatformAdmin,
  onOpenSubscription,
  onOpenCases,
  onOpenEpma,
  onSelectPatient,
  onLoginClick,
  onCreateAccountClick,
  onForgotPassword,
  onLogout,
  onUpdateDiscipline,
  onNotificationClick,
  activeSessionCode,
  sessionInput,
  onSessionInputChange,
  onJoinLive,
  studentError,
  recentPatientAccesses = [],
}) => {
  const { tenant } = useCurrentTenant();
  const brandLabel = tenant?.displayName || 'MediCase';
  const brandLogoUrl = tenant?.brandLogoUrl || '';
  const [showLiveJoin, setShowLiveJoin] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const recentCharts = recentPatientAccesses.slice(0, 5);

  // Notification bell - polls every 60s while logged in. NavBar owns
  // this state since the bell lives here; App.jsx only handles the
  // post-click navigation via onNotificationClick.
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);
  const refreshNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listNotifications(token);
      setNotifications(Array.isArray(result?.notifications) ? result.notifications : []);
      setUnreadCount(Number(result?.unreadCount) || 0);
    } catch {
      // Soft-fail - polling will try again
    }
  }, [token]);
  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }
    refreshNotifications();
    pollRef.current = setInterval(refreshNotifications, NOTIFICATIONS_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [token, refreshNotifications]);
  const handleNotificationItemClick = async (n) => {
    if (n && !n.readAt && token) {
      try {
        await api.markNotificationRead(n.id, token);
        setNotifications((prev) => prev.map((row) => (
          row.id === n.id ? { ...row, readAt: new Date().toISOString() } : row
        )));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // Continue - we'll still navigate
      }
    }
    if (typeof onNotificationClick === 'function') {
      onNotificationClick(n);
    }
  };
  const handleMarkAllRead = async () => {
    if (!token || unreadCount === 0) return;
    try {
      await api.markAllNotificationsRead(token);
      const now = new Date().toISOString();
      setNotifications((prev) => prev.map((row) => (
        row.readAt ? row : { ...row, readAt: now }
      )));
      setUnreadCount(0);
    } catch {
      // No-op
    }
  };
  const isEducatorUser = user?.role === 'educator' || user?.role === 'educator_admin';
  const isEducatorAdmin = user?.role === 'educator_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';
  const subscriptionStatus = user?.subscriptionStatus || 'free';
  const isOnPaywallTenant = tenant?.tenantType === 'central_students';
  const isFreeTier = isOnPaywallTenant && !isEducatorUser && !isPlatformAdmin
    && subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing';
  const isSubscribed = isOnPaywallTenant && !isEducatorUser && !isPlatformAdmin
    && (subscriptionStatus === 'active' || subscriptionStatus === 'trialing');
  const accountRoleLabel = {
    student: 'Student',
    educator: 'Educator',
    educator_admin: 'Facilitator admin',
    platform_admin: 'Platform admin',
  }[user?.role] || user?.role || 'Not recorded';
  const handleBrandClick = (event) => {
    event.preventDefault();

    if (!user) {
      onViewChange('public');
      return;
    }

    if (isPlatformAdmin) {
      onViewChange('platform-admin');
      return;
    }

    if (isEducatorUser) {
      onViewChange('educator-home');
      return;
    }

    onViewChange('student-home');
  };

  // Bell rendering is identical inside the collapsible nav (desktop)
  // and outside it (mobile). Wrapping in a function lets us position
  // an always-visible bell next to the hamburger toggle while keeping
  // the desktop bell in its existing slot next to the account menu.
  const renderBell = (idSuffix, extraClassName = '') => (
    <NavDropdown
      title={
        <span className="app-shell-nav__bell">
          <i className="bi bi-bell-fill" aria-hidden="true" />
          <span className="visually-hidden">Notifications</span>
          {unreadCount > 0 ? (
            <Badge bg="danger" pill className="app-shell-nav__bell-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          ) : null}
        </span>
      }
      id={`notifications-dropdown-${idSuffix}`}
      menuVariant="light"
      align="end"
      className={`app-shell-nav__dropdown app-shell-nav__bell-dropdown ${extraClassName}`}
    >
      <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
        <strong className="small">Notifications</strong>
        {unreadCount > 0 ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 small"
            onClick={(ev) => { ev.preventDefault(); handleMarkAllRead(); }}
          >
            Mark all read
          </Button>
        ) : null}
      </div>
      {notifications.length === 0 ? (
        <div className="px-3 py-3 small text-muted text-center">
          No notifications yet.
        </div>
      ) : (
        <div className="app-shell-nav__bell-list">
          {notifications.map((n) => {
            const isUnread = !n.readAt;
            const actor = n.actorName || 'Someone';
            let line = '';
            if (n.type === 'comment_received') {
              line = `${actor} left feedback on your portfolio entry`;
            } else if (n.type === 'student_entry_added') {
              const portfolio = n.metadata?.portfolioName ? ` to “${n.metadata.portfolioName}”` : '';
              line = `${actor} added a new entry${portfolio}`;
            } else {
              line = `${actor}: ${n.type}`;
            }
            return (
              <NavDropdown.Item
                key={n.id}
                onClick={() => handleNotificationItemClick(n)}
                className={`app-shell-nav__bell-item ${isUnread ? 'app-shell-nav__bell-item--unread' : ''}`}
              >
                <div className="d-flex align-items-start gap-2">
                  {isUnread ? <span className="app-shell-nav__bell-dot" /> : <span className="app-shell-nav__bell-dot app-shell-nav__bell-dot--read" />}
                  <div className="flex-grow-1">
                    <div className="small fw-semibold" style={{ whiteSpace: 'normal' }}>{line}</div>
                    {n.bodySnippet ? (
                      <div className="small text-muted" style={{ whiteSpace: 'normal' }}>
                        {n.bodySnippet}
                      </div>
                    ) : null}
                    <div className="small text-muted">{formatNotificationTime(n.createdAt)}</div>
                  </div>
                </div>
              </NavDropdown.Item>
            );
          })}
        </div>
      )}
    </NavDropdown>
  );

  return (
    <>
      <Navbar className="app-shell-nav py-3" expand="md">
        <Container fluid>
          <Navbar.Brand href="#" onClick={handleBrandClick} className="app-shell-nav__brand fw-bold d-flex align-items-center gap-2">
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt={brandLabel} className="app-shell-nav__brand-logo" />
            ) : null}
            <span>{brandLabel}</span>
          </Navbar.Brand>
          {user ? (
            <Nav className="d-md-none ms-auto me-2 app-shell-nav__mobile-bell">
              {renderBell('mobile')}
            </Nav>
          ) : null}
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-between align-items-center gap-3">
            <Nav className="me-auto gap-2">
              {!user ? null : isPlatformAdmin ? (
                <Button type="button" variant="light" className={`app-shell-nav__button ${currentView === 'platform-admin' ? 'app-shell-nav__button--active' : ''}`} onClick={onOpenPlatformAdmin}>
                  Platform admin
                </Button>
              ) : isEducatorUser ? (
                <>
                </>
              ) : (
                <>
                </>
              )}
            </Nav>
            <Nav className="align-items-center gap-3">
              {user ? (
                <>
                  {onOpenEpma ? (
                    <NavDropdown title="Previously Accessed Patients" id="student-patient-dropdown" menuVariant="light" align="end" className="app-shell-nav__dropdown">
                      <NavDropdown.Item onClick={onOpenEpma}>Find a patient</NavDropdown.Item>
                      <NavDropdown.Divider />
                      {recentCharts.length ? recentCharts.map((patient) => (
                        <NavDropdown.Item key={patient.id} onClick={() => onSelectPatient(patient.id)}>
                          <div className="fw-semibold">{patient.fullName}</div>
                          <div className="small text-muted">
                            {patient.lastAccessedAt ? `Viewed ${new Date(patient.lastAccessedAt).toLocaleString('en-GB')}` : 'Previously viewed'}
                          </div>
                        </NavDropdown.Item>
                      )) : (
                        <NavDropdown.Item disabled>No recent charts</NavDropdown.Item>
                      )}
                    </NavDropdown>
                  ) : null}
                  {renderBell('desktop', 'd-none d-md-block')}
                  <NavDropdown
                    title={
                      <span className="d-inline-flex align-items-center gap-2">
                        <span>{user.displayName || user.email}</span>
                        {isFreeTier ? <Badge bg="warning" text="dark"><i className="bi bi-lock-fill me-1" />Free tier</Badge> : null}
                        {isSubscribed ? <Badge bg="success">Subscribed</Badge> : null}
                      </span>
                    }
                    id="account-settings-dropdown"
                    menuVariant="light"
                    align="end"
                    className="app-shell-nav__dropdown app-shell-nav__account-dropdown"
                  >
                    <NavDropdown.Item onClick={() => setShowAccountSettings(true)}>Account settings</NavDropdown.Item>
                    <NavDropdown.Item onClick={onForgotPassword}>Reset password</NavDropdown.Item>
                    {tenant?.tenantType === 'central_students' && !isEducatorUser && !isPlatformAdmin && onOpenSubscription ? (
                      <NavDropdown.Item onClick={onOpenSubscription}>Subscription</NavDropdown.Item>
                    ) : null}
                    <NavDropdown.Divider />
                    <NavDropdown.Item onClick={onLogout}>Log out</NavDropdown.Item>
                  </NavDropdown>
                </>
              ) : (
                <>
                  <Button type="button" variant="light" className="app-shell-nav__button" onClick={onLoginClick}>Sign in</Button>
                  <Button type="button" variant="light" className="app-shell-nav__button" onClick={onCreateAccountClick}>Create account</Button>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Modal show={showLiveJoin} onHide={() => setShowLiveJoin(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Join a live teaching session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(event) => {
              const joined = onJoinLive(event);
              if (joined) {
                setShowLiveJoin(false);
              }
            }}
          >
            <Form.Group className="mb-3" controlId="navSessionCode">
              <Form.Label>Live session code</Form.Label>
              <Form.Control
                type="text"
                value={sessionInput}
                onChange={(event) => onSessionInputChange(event.target.value.toUpperCase())}
                placeholder="e.g. A1B2C3"
              />
            </Form.Group>
            {studentError ? <Alert variant="warning">{studentError}</Alert> : null}
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" variant="outline-secondary" onClick={() => setShowLiveJoin(false)}>Cancel</Button>
              <Button type="submit">Join session</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showAccountSettings} onHide={() => setShowAccountSettings(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Account settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="account-settings-readonly">
            <div className="account-settings-readonly__row">
              <span>Name</span>
              <strong>{user?.displayName || 'Not recorded'}</strong>
            </div>
            <div className="account-settings-readonly__row">
              <span>Email</span>
              <strong>{user?.email || 'Not recorded'}</strong>
            </div>
            <div className="account-settings-readonly__row">
              <span>Role</span>
              <strong>{accountRoleLabel}</strong>
            </div>
          </div>
          {user?.role === 'student' && typeof onUpdateDiscipline === 'function' ? (
            <Form.Group className="mt-3" controlId="account-discipline">
              <Form.Label>Study area</Form.Label>
              <Form.Select
                value={user?.discipline || ''}
                onChange={(event) => onUpdateDiscipline(event.target.value || null)}
              >
                <option value="">Prefer not to say</option>
                <option value="medical">Medical / medicine</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="nursing">Nursing</option>
                <option value="other">Other</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Cases that are tagged for specific disciplines will use this to show you only the relevant questions. Set to Prefer not to say to see every question on every case.
              </Form.Text>
            </Form.Group>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setShowAccountSettings(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NavBar;
